import get from 'lodash/get'
import Card from '../Card'
import Availability, { CardAvailabilityInfo } from './Availability'
import css from './meetingRoomCard.module.scss'
import {
  DateUtil,
  hasDeptManageRights,
  hasLocationManageRights,
  isAdmin,
  isDepartmentAdmin,
  isFinder,
  isLocationAdmin,
  isMeetingRoom,
  isPowerAdmin,
  isSpaceCaptain,
} from '@utils'
import { useFloor } from '@providers/mapProvider'
import { useUser } from '@hooks'
import api, { ISpaceDetails, IUploadedImageAsset } from '@api'
import { useBookingDesk } from '@providers'
import { getSpaceImage } from 'components/AdminFloorPlan/SpaceCatalog'
import { Button } from '@runeffective/evo-design-system'
import { useEffect, useState } from 'react'
import { IconButton, Toaster } from '@ui'
import getSpaceAsset from 'components/SpaceTypeManagement/hooks/useSpaceAsset'
import { ApolloError } from 'apollo-client'
import { handleError } from '@ui/canvas/utils'

const placeholder = '/public/meeting-room-placeholder.png'
const noCalendarMessage = 'Space not associated to calendar. Please contact your admin.'

interface MeetingRoomT {
  data: ISpaceDetails
  toggleModal?: () => void
}

const MeetingRoomCard = ({ data, toggleModal }: MeetingRoomT) => {
  const floor = useFloor()
  const { user } = useUser()
  const { date } = useBookingDesk()
  const floorId = floor.floorId || ''
  const {
    role,
    employee: { assets },
  } = user || { employee: {} }
  const { id: spaceId, name, tags, capacity, bookings, spaceType } = data

  const admin = isAdmin(role) || isPowerAdmin(role)
  const hasPermissions =
    (isLocationAdmin(role) && hasLocationManageRights(user?.employee, floorId)) ||
    (isDepartmentAdmin(role) && hasDeptManageRights(user?.employee, data)) ||
    (isSpaceCaptain(role) &&
      hasLocationManageRights(user?.employee, floorId) &&
      hasDeptManageRights(user?.employee, data))

  // Meeting rooms can book any role
  const canBook = admin || hasPermissions || floor.isBookMeetingRoomPage

  const isAvailable = floor?.spaces?.byId[data.id].bookings?.length ? false : true
  const summary = data?.bookings?.[0]?.summary
  // (data &&
  //   data.dailyBookableSpacesAvailability.length &&
  //   booking.dailyBookableSpacesAvailability[0] &&
  //   booking.dailyBookableSpacesAvailability[0].bookings.length &&
  //   booking.dailyBookableSpacesAvailability[0].bookings[0].summary) ||
  // null

  const isCalendarMissing = floor.spaces.byId[data.id].calendar === null ? true : false
  const [imgIndx, setImgIndx] = useState(0)

  const deskType = isMeetingRoom(spaceType) ? 'Meeting Room' : ''
  const [imgAssets, setImgAssets] = useState<IUploadedImageAsset[]>([])
  useEffect(() => {
    getSpaceAsset(spaceId).then((assets) => {
      setImgAssets(assets)
    })
  }, [spaceId])
  const setValidImgIndx = (indx: number) => {
    let val = imgIndx + indx
    val = val < 0 ? 0 : val === imgAssets.length ? imgAssets.length - 1 : val
    setImgIndx(val)
  }

  return (
    <Card>
      <div className={css.blockGap}>
        <div className={css.imageContainer}>{getSpaceImage(imgAssets[imgIndx]?.url, data)}</div>
        {imgAssets[imgIndx] && (
          <div style={{ marginTop: 14, display: 'flex', justifyContent: 'space-evenly' }}>
            {
              <IconButton
                style={{ marginBottom: 8 }}
                round
                minimal
                disabled={imgIndx === 0}
                iconName="arrow-left"
                // tooltip="Previous"
                // tooltipPosition={Position.LEFT}
                onClick={() => setValidImgIndx(-1)}
              />
            }
            <div className="pt-1">
              {imgIndx + 1} / {imgAssets.length}
            </div>
            {
              <IconButton
                style={{ marginBottom: 8 }}
                round
                minimal
                disabled={imgIndx + 1 === imgAssets.length}
                iconName="arrow-right"
                // tooltip="Next"
                // tooltipPosition={Position.RIGHT}
                onClick={() => setValidImgIndx(1)}
              />
            }
          </div>
        )}
      </div>
      <div className={css.container}>
        <Card.Details>
          <Card.Title>{deskType}</Card.Title>
          <Card.Row label="capacity:" value={capacity?.toString()} />
          {tags.length ? (
            <Card.Row label="assets:" value={tags.map((a) => a.name).join(', ')} />
          ) : null}
          {floor.isBookMeetingRoomPage && !isCalendarMissing && summary && (
            <>
              <Card.Row label="summary:" value={summary as string} />
              <CardAvailabilityInfo status="Occupied" width={100} />
            </>
          )}

          {!floor.isBookMeetingRoomPage && isAvailable && (
            <>
              <div>Availability:</div>
              <Availability id={spaceId} device={''} addUserBookingSuccess={() => {}} />
            </>
          )}
        </Card.Details>
        {floor.isBookMeetingRoomPage && isCalendarMissing && (
          <CardAvailabilityInfo
            status={noCalendarMessage}
            width={isCalendarMissing ? 152 : undefined}
            capitalize={!isCalendarMissing}
          />
        )}
        {!isFinder(user?.role) && (
          <div style={{ position: 'absolute', right: 5 }}>
            <IconButton
              style={{ marginBottom: 8 }}
              round
              minimal
              small
              iconName="times"
              onClick={toggleModal}
            />
          </div>
        )}
        {floor.isBookMeetingRoomPage && isAvailable && canBook && !isCalendarMissing && (
          <span className={css.buttonContainer}>
            <Button
              size="small"
              layout="secondary"
              style={{ display: 'inline', marginRight: '10px' }}
              onClick={toggleModal}>
              Cancel
            </Button>
            <Button
              size="small"
              layout="primary"
              style={{ marginRight: '10px' }}
              onClick={() => {
                api.space
                  .createCalendarEvent(data.id, date?.fromDate, date?.toDate as DateUtil)
                  .then(() => {
                    floor.reloadMap?.(date.fromDate, date.toDate)
                  })
                  .catch(handleError)
              }}>
              Book Now
            </Button>
          </span>
        )}
      </div>
    </Card>
  )
}

export const WayfinderMeetingRoomCard = (props: any) => <MeetingRoomCard {...props} />

export const FinderMeetingRoomCardUnconnected = (props: any) => <MeetingRoomCard {...props} />
